import { PhoneInput, TextInput, useSnackbar } from "@fullscript/aviary-web";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { getPhoneWithoutCountryCode } from "@aviary/components/PhoneInput";
import { COUNTRY_CODE_TO_COUNTRY } from "@aviary/components/PhoneInput/CountrySelector/Countries";
import { isValidPhoneNumber } from "@aviary/components/PhoneInput/validatePhoneNumber";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import { ChooseAuthenticationMethod } from "@shared/MultifactorAuthentication/components/ChooseAuthenticationMethod/ChooseAuthenticationMethod";
import {
  useEnableMfaSharedMutation,
  type EnableMfaSharedMutation,
} from "@shared/MultifactorAuthentication/data/EnableMFA.mutation.generated";
import {
  useSetupMfaSharedMutation,
  type SetupMfaSharedMutation,
} from "@shared/MultifactorAuthentication/data/SetupMFA.mutation.generated";
import { DisplayBackupCode } from "@shared/MultifactorAuthentication/steps/DisplayBackupCode/DisplayBackupCode";
import { DownloadAuthenticator } from "@shared/MultifactorAuthentication/steps/DownloadAuthenticator/DownloadAuthenticator";
import { EnableWithAuthenticator } from "@shared/MultifactorAuthentication/steps/EnableWithAuthenticator/EnableWithAuthenticator";
import { EnableWithEmail } from "@shared/MultifactorAuthentication/steps/EnableWithEmail/EnableWithEmail";
import { EnableWithSMS } from "@shared/MultifactorAuthentication/steps/EnableWithSMS/EnableWithSMS";
import { EnterPhoneNumber } from "@shared/MultifactorAuthentication/steps/EnterPhoneNumber/EnterPhoneNumber";
import {
  type AuthFlow,
  smsAuthFlow,
  type MFAStepDetails,
  authenticatorAuthFlow,
  type CurrentMFAProgress,
  emailAuthFlow,
} from "@shared/MultifactorAuthentication/steps/MFASteps";
import { SecureYourAccount } from "@shared/MultifactorAuthentication/steps/SecureYourAccount/SecureYourAccount";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import { TwoFactorAuthenticationMethods as AuthMethod } from "@shared/types/graphqlGenerated";

interface HistoryState {
  mfaToken?: string;
}

interface Props {
  /** Which authentication method user currently has enabled (if any) */
  existingAuthFlow?: AuthFlow;
  /** Navigation for the flow is different in modal vs fullpage */
  renderNavigation: (currentProgress: CurrentMFAProgress) => ReactElement;
  /** the identifier for the current MFA 'instance' */
  mfaToken?: string;
  /** The token received from a successful verification during the change flow */
  verificationToken?: string;
  /** Whether flow is rendered via modal or fullpage */
  isModal?: boolean;
}

const SetupMFAFlow = ({
  existingAuthFlow,
  renderNavigation,
  mfaToken,
  verificationToken,
  isModal,
}: Props) => {
  const { criticalSnackbar, successSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { country } = useSharedGlobalConfig();
  const preferredCountry = country === "United States" ? "US" : "CA";
  const { state }: { state: HistoryState } = useLocation();
  const token = state?.mfaToken || mfaToken;

  const getDefaultAuthFlow = () => {
    if (!existingAuthFlow) return smsAuthFlow;
    // when changing MFA from user email, can't change to user email
    if (existingAuthFlow.authMethod === AuthMethod.Email && verificationToken) return smsAuthFlow;

    return existingAuthFlow;
  };

  const [currentAuthFlow, setCurrentAuthFlow] = useState<AuthFlow>(getDefaultAuthFlow());
  const [currentStep, setCurrentStep] = useState<MFAStepDetails>(getDefaultAuthFlow().steps[0]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState(preferredCountry);
  const [emailMasked, setEmailMasked] = useState("");
  const [oneTimePasscode, setOneTimePasscode] = useState("");
  const [lastSubmittedCode, setLastSubmittedCode] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [manualSetupCode, setManualSetupCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [redirectPath, setRedirectPath] = useState("");

  const goToNextStep = () => {
    const nextStep = currentAuthFlow.steps.find(s => s.step === currentStep.nextStep);
    setCurrentStep(nextStep || currentStep);
  };

  const [setupMFA, { loading: loadingSetupMFA }] = useSetupMfaSharedMutation({
    onCompleted: data => handleSetupMFAComplete(data),
    onError: ({ message }) => criticalSnackbar(message),
  });

  const [enableMFA, { loading: loadingEnableMFA }] = useEnableMfaSharedMutation({
    onCompleted: data => handleEnableMFAComplete(data),
    onError: ({ message }) => criticalSnackbar(message),
    refetchQueries: ["MFA_Shared_Query"],
  });

  const handleSetupMFAComplete = (data: SetupMfaSharedMutation) => {
    const payload = data?.auth?.userSetupTwoFactorAuthentication;
    const url = payload?.qrCodeUrl;
    const manualCode = payload?.manualSetupCode;
    const twoFactorAuthentication = payload?.twoFactorAuthentication;
    const errors = payload?.errors;

    if (errors?.message) {
      criticalSnackbar(t(l.mfa.errorOccurredTryAgain));
    } else {
      setQrCodeUrl(url ?? "");
      setManualSetupCode(manualCode ?? "");
      setEmailMasked(twoFactorAuthentication?.emailMasked || "");
      goToNextStep();
    }
  };

  const handleEnableMFAComplete = (data: EnableMfaSharedMutation) => {
    const payload = data?.auth?.userEnableTwoFactorAuthentication;
    const newBackupCode = payload?.newBackupCode;
    const twoFactorAuthentication = payload?.twoFactorAuthentication;
    const errors = payload?.errors;
    const path = payload?.redirectPath;

    if (errors?.message || !twoFactorAuthentication?.isEnabled) {
      criticalSnackbar(t(l.mfa.codeDoesNotMatch));
    } else {
      setBackupCode(newBackupCode ?? "");
      setRedirectPath(path ?? "");
      successSnackbar(t(l.mfa.enableSuccess));
      goToNextStep();
    }
  };

  const handleAuthMethodSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === AuthMethod.Sms) {
      setCurrentAuthFlow(smsAuthFlow);
      setCurrentStep(smsAuthFlow.steps[0]);
    } else if (event.target.value === AuthMethod.App) {
      setCurrentAuthFlow(authenticatorAuthFlow);
      setCurrentStep(authenticatorAuthFlow.steps[0]);
    } else if (event.target.value === AuthMethod.Email) {
      setCurrentAuthFlow(emailAuthFlow);
      setCurrentStep(emailAuthFlow.steps[0]);
    }
  };

  const handleStepChange = (isPrevious?: boolean) => {
    if (isPrevious) {
      const prevStep = currentAuthFlow.steps.find(s => s.step === currentStep.previousStep);
      setCurrentStep(prevStep || currentStep);

      // Clear codes when navigating back so code is not prefilled when changing MFA option
      setOneTimePasscode("");
      setLastSubmittedCode("");

      return;
    }

    fireActionForStep();
  };

  const normalizePhoneNumber = (phone: string): string => {
    // If phone already has country code prefix, return as-is
    if (phone.startsWith("+")) return phone;

    // Otherwise, prepend the selected country's dial code (handles edge cases like browser autofill)
    const selectedCountry = COUNTRY_CODE_TO_COUNTRY[selectedCountryCode];
    const dialCode = selectedCountry?.dialCode || "1";
    const digitsOnly = phone.replace(/\D/g, "");

    return `+${dialCode}${digitsOnly}`;
  };

  const fireActionForStep = () => {
    if (currentStep.action === "setupMFA") {
      // email && app flows don't use otpMethodField
      const includeMethodField = currentAuthFlow.authMethod === AuthMethod.Sms;
      setupMFA({
        variables: {
          input: {
            otpMethod: currentAuthFlow.authMethod,
            otpMethodField: includeMethodField ? normalizePhoneNumber(phoneNumber) : null,
            token,
            ...(verificationToken && { verificationToken }),
          },
        },
      });
    } else if (currentStep.action === "enableMFA") {
      setLastSubmittedCode(oneTimePasscode);
      enableMFA({
        variables: {
          input: {
            otp: oneTimePasscode,
            token,
            ...(verificationToken && { verificationToken }),
          },
        },
      });
    } else {
      goToNextStep();
    }
  };

  const shouldNextBeDisabled = () => {
    if (currentStep.step === "enterMobilePhone") {
      return !isValidPhoneNumber(getPhoneWithoutCountryCode(phoneNumber));
    }
    if (
      currentStep.step === "enableWithSMS" ||
      currentStep.step === "enableWithAuthenticator" ||
      currentStep.step === "enableWithEmail"
    ) {
      return !oneTimePasscode || oneTimePasscode === lastSubmittedCode;
    }

    return false;
  };

  const skippable = () => {
    // if verificationToken exists, they're using the change flow
    if (verificationToken) {
      return false;
    } else if (!mfaToken) {
      return true;
    } else return false;
  };

  const getPhoneLast4 = () => {
    const phone = getPhoneWithoutCountryCode(phoneNumber);
    if (!phone || phone.length < 5) {
      return phone;
    }

    return phone.slice(-4);
  };

  const isNextDisabled = shouldNextBeDisabled();
  const isLoading = loadingSetupMFA || loadingEnableMFA;

  const renderCurrentStep = () => {
    switch (currentStep.step) {
      case "chooseAuthenticationMethod":
        return (
          <SecureYourAccount
            isSkippable={skippable()}
            isChangeFlow={!!verificationToken}
            isModal={isModal}
          >
            <ChooseAuthenticationMethod
              selectedAuthMethod={currentAuthFlow.authMethod}
              onSelect={handleAuthMethodSelect}
            />
          </SecureYourAccount>
        );
      case "downloadAuthenticator":
        return <DownloadAuthenticator isModal={isModal} />;
      case "enterMobilePhone":
        return (
          <EnterPhoneNumber isModal={isModal}>
            <PhoneInput
              id={t(l.mfa.enterMobilePhone.number)}
              label={t(l.mfa.enterMobilePhone.number)}
              aria-label={t(l.mfa.enterMobilePhone.number)}
              value={phoneNumber}
              onChange={event => setPhoneNumber(event.target.value)}
              onCountryCodeChange={countryCode => setSelectedCountryCode(countryCode)}
              defaultCountryCode={preferredCountry}
              errorCaption={undefined}
              required
            />
          </EnterPhoneNumber>
        );
      case "enableWithSMS":
        return (
          <EnableWithSMS phoneLast4={getPhoneLast4()} isModal={isModal}>
            <TextInput
              id="oneTimePasscodeSMS"
              type="text"
              value={oneTimePasscode}
              label={t(l.mfa.oneTimeCode)}
              onChange={e => setOneTimePasscode(e.target.value)}
              errorCaption={undefined}
              required
            />
          </EnableWithSMS>
        );
      case "enableWithEmail":
        return (
          <EnableWithEmail emailMasked={emailMasked} isModal={isModal}>
            <TextInput
              id="oneTimePasscodeEmail"
              type="text"
              value={oneTimePasscode}
              label={t(l.mfa.oneTimeCode)}
              onChange={e => setOneTimePasscode(e.target.value)}
              errorCaption={undefined}
              required
            />
          </EnableWithEmail>
        );
      case "enableWithAuthenticator":
        return (
          <EnableWithAuthenticator
            qrCodeUrl={qrCodeUrl}
            manualSetupCode={manualSetupCode}
            isModal={isModal}
          >
            <TextInput
              id="oneTimePasscodeAuthenticator"
              type="text"
              value={oneTimePasscode}
              label={t(l.mfa.oneTimeCode)}
              onChange={e => setOneTimePasscode(e.target.value)}
              errorCaption={undefined}
              required
            />
          </EnableWithAuthenticator>
        );
      case "displayBackupCode":
        return <DisplayBackupCode backupCode={backupCode} isModal={isModal} />;
    }
  };

  return (
    <>
      {renderCurrentStep()}
      {renderNavigation({
        currentStep,
        handleStepChange,
        isNextDisabled,
        isLoading,
        mfaToken,
        redirectPath,
      })}
    </>
  );
};

export { SetupMFAFlow };
